'use client'

import React from 'react'

import { ArrowButton } from '@/components/ui/arrow-button'
import { useCarouselScroll } from '@/hooks/useCarouselScroll'
import { cn } from '@/utilities/ui'

import { FeatureCard, type FeatureCardData, type FeatureCardStyle } from './FeatureCard'

interface FeatureCardsCarouselProps {
  style: FeatureCardStyle
  cards: FeatureCardData[]
  autoplayDelay?: number
  ariaLabel?: string
}

const DESKTOP_MAX = 4
const TABLET_MAX = 3

/**
 * Renders feature cards.
 *
 * Per breakpoint:
 *  - desktop (lg+): static 4-col grid when cards ≤ 4, else carousel
 *  - tablet (md..lg): static 3-col grid when cards ≤ 3, else carousel
 *  - mobile (<md): static single column when cards = 1, else carousel
 */
export const FeatureCardsCarousel: React.FC<FeatureCardsCarouselProps> = ({
  style,
  cards,
  autoplayDelay = 0,
  ariaLabel = 'Feature cards carousel',
}) => {
  if (!cards || cards.length === 0) return null

  const desktopIsCarousel = cards.length > DESKTOP_MAX
  const tabletIsCarousel = cards.length > TABLET_MAX
  const mobileIsCarousel = cards.length > 1
  const needsCarouselAtAnyBreakpoint =
    mobileIsCarousel || tabletIsCarousel || desktopIsCarousel

  return (
    <div className="flex flex-col gap-6 lg:gap-8 w-full">
      {/* Desktop grid: cards ≤ 4 */}
      {!desktopIsCarousel && (
        <div className="hidden lg:grid lg:grid-cols-4 lg:gap-6 w-full items-stretch">
          {cards.map((card, i) => (
            <FeatureCard key={card.id ?? i} card={card} style={style} />
          ))}
        </div>
      )}

      {/* Tablet grid: cards ≤ 3 (hidden on lg+) */}
      {!tabletIsCarousel && (
        <div className="hidden md:grid lg:hidden md:grid-cols-3 md:gap-6 w-full items-stretch">
          {cards.map((card, i) => (
            <FeatureCard key={card.id ?? i} card={card} style={style} />
          ))}
        </div>
      )}

      {/* Mobile single-card stack: only when there is exactly 1 card */}
      {!mobileIsCarousel && (
        <div className="md:hidden w-full">
          {cards.map((card, i) => (
            <FeatureCard key={card.id ?? i} card={card} style={style} />
          ))}
        </div>
      )}

      {needsCarouselAtAnyBreakpoint && (
        <CarouselTrack
          style={style}
          cards={cards}
          autoplayDelay={autoplayDelay}
          ariaLabel={ariaLabel}
          desktopIsCarousel={desktopIsCarousel}
          tabletIsCarousel={tabletIsCarousel}
          mobileIsCarousel={mobileIsCarousel}
        />
      )}
    </div>
  )
}

interface CarouselTrackProps extends FeatureCardsCarouselProps {
  desktopIsCarousel: boolean
  tabletIsCarousel: boolean
  mobileIsCarousel: boolean
}

const CarouselTrack: React.FC<CarouselTrackProps> = ({
  style,
  cards,
  autoplayDelay = 0,
  ariaLabel,
  desktopIsCarousel,
  tabletIsCarousel,
  mobileIsCarousel,
}) => {
  const { scrollRef, activeIndex, isAtStart, isAtEnd, scrollToIndex, handleKeyDown } =
    useCarouselScroll({
      itemCount: cards.length,
      autoplayDelay,
    })

  // Show this carousel only at breakpoints that need it.
  const visibilityClass = cn(
    mobileIsCarousel ? 'flex' : 'hidden',
    tabletIsCarousel ? 'md:flex' : 'md:hidden',
    desktopIsCarousel ? 'lg:flex' : 'lg:hidden',
    'flex-col gap-4 w-full',
  )

  return (
    <div className={visibilityClass}>
      <div
        ref={scrollRef}
        role="region"
        aria-label={ariaLabel}
        aria-roledescription="carousel"
        tabIndex={0}
        onKeyDown={handleKeyDown}
        className={cn(
          'relative flex overflow-x-auto scroll-smooth snap-x snap-mandatory scrollbar-hide w-full gap-6',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-civo-lime focus-visible:ring-offset-2',
        )}
      >
        {cards.map((card, index) => (
          <div
            key={card.id ?? index}
            data-card
            role="group"
            aria-roledescription="slide"
            aria-label={`${index + 1} of ${cards.length}`}
            className={cn(
              'shrink-0 snap-start',
              // Mobile: ~85% width leaves a peek of the next card.
              // Tablet: 3-up, subtracting the two 24px gaps.
              // Desktop: 4-up, subtracting the three 24px gaps.
              'basis-[85%] md:basis-[calc((100%-48px)/3)] lg:basis-[calc((100%-72px)/4)]',
            )}
          >
            <FeatureCard card={card} style={style} />
          </div>
        ))}
      </div>

      <div
        className="flex items-center gap-2 self-end"
        role="group"
        aria-label="Carousel navigation"
      >
        <ArrowButton
          direction="left"
          backgroundColor="lime"
          size="medium"
          animated
          onClick={() => scrollToIndex(activeIndex - 1)}
          disabled={isAtStart}
          aria-label="Previous slide"
        />
        <ArrowButton
          direction="right"
          backgroundColor="lime"
          size="medium"
          animated
          onClick={() => scrollToIndex(activeIndex + 1)}
          disabled={isAtEnd}
          aria-label="Next slide"
        />
      </div>

      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {`Slide ${activeIndex + 1} of ${cards.length}`}
      </div>
    </div>
  )
}
